import type {
  DropboxArchiveStatus,
  Invoice,
  InvoiceListResponse,
  InvoiceStatus,
  ReferenceData,
  Supplier,
  ZohoAccount,
  ZohoBillTemplate,
  ZohoItem,
  ZohoVendor,
} from '../types';

/**
 * Client API minimal. Toutes les requêtes passent par /api (proxy Vite en dev,
 * served directement par Hono en prod — pas de CORS à gérer).
 */

// Exporté pour `ndf-api.ts` : le module Notes de frais a son propre client,
// mais doit hériter du même traitement d'erreur — notamment la redirection sur
// session expirée, qu'on ne veut surtout pas voir diverger entre modules.
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* corps non-JSON, on ignore */
    }
    let message = `${res.status} ${res.statusText}`;
    if (
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof body.error === 'string'
    ) {
      message = body.error;
    }
    // 401 → session expirée. On redirige vers /login pour ne pas
    // laisser l'UI dans un état cassé. (Skip si on est déjà sur /login.)
    if (res.status === 401 && typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login?error=session_expired');
    }
    throw new ApiError(message, res.status, body);
  }
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Résultat normalisé d'un upload de facture. Volontairement SANS throw : l'appel
 * en masse a besoin d'un statut par fichier (doublon et erreurs sont des issues
 * légitimes, pas des exceptions à propager).
 */
export type UploadOutcome =
  | { ok: true; id: string }
  | { ok: false; kind: 'duplicate'; existingId: string; message: string }
  | { ok: false; kind: 'error'; message: string; httpStatus: number };

/**
 * Upload d'UN fichier facture (multipart). Contourne `request<T>()` car on ne
 * veut pas throw sur 409/4xx (statut par fichier) et pas de header
 * Content-Type (le navigateur pose le boundary multipart lui-même).
 * Le rappel N fois (séquentiel) côté UI réalise l'upload « en masse ».
 */
async function uploadInvoiceFile(file: File): Promise<UploadOutcome> {
  const fd = new FormData();
  fd.append('file', file);
  let res: Response;
  try {
    res = await fetch('/api/invoices/upload', {
      method: 'POST',
      credentials: 'same-origin',
      body: fd,
    });
  } catch (err) {
    return {
      ok: false,
      kind: 'error',
      message: err instanceof Error ? err.message : 'Réseau indisponible',
      httpStatus: 0,
    };
  }
  if (res.ok) {
    const data = (await res.json()) as { id: string };
    return { ok: true, id: data.id };
  }
  // Corps d'erreur best-effort (peut ne pas être du JSON).
  let message = `${res.status} ${res.statusText}`;
  let existingId: string | null = null;
  try {
    const body = (await res.json()) as {
      error?: string;
      duplicate?: boolean;
      existing_id?: string;
    };
    if (body.error) message = body.error;
    if (body.duplicate && body.existing_id) existingId = body.existing_id;
  } catch {
    /* corps non-JSON */
  }
  if (res.status === 409 && existingId) {
    return { ok: false, kind: 'duplicate', existingId, message };
  }
  return { ok: false, kind: 'error', message, httpStatus: res.status };
}

// ─── Invoices ─────────────────────────────────────────────────────────────
export const invoicesApi = {
  upload: uploadInvoiceFile,
  list: (params: { status?: InvoiceStatus; limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.status) search.set('status', params.status);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    return request<InvoiceListResponse>(`/api/invoices${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => request<{ invoice: Invoice }>(`/api/invoices/${id}`),
  validate: (id: string, payload: object) =>
    request<{ invoice: Invoice }>(`/api/invoices/${id}/validate`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  reject: (id: string, reason: string) =>
    request<{ invoice: Invoice }>(`/api/invoices/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  extract: (id: string) =>
    request<{ id: string; status: string; message: string }>(
      `/api/invoices/${id}/extract`,
      { method: 'POST' },
    ),
  create: (id: string) =>
    request<{
      ok: boolean;
      zoho_bill_id: string;
      dropbox_archive_status: DropboxArchiveStatus;
      dropbox_archive_path: string | null;
      archive_error: string | null;
      dropbox_accountant_path: string | null;
      accountant_error: string | null;
    }>(`/api/invoices/${id}/create`, { method: 'POST' }),
  /** Réessaie UNIQUEMENT l'archivage Dropbox (la bill Zoho existe déjà). */
  archive: (id: string) =>
    request<{
      ok: boolean;
      dropbox_archive_status: DropboxArchiveStatus;
      dropbox_archive_path: string | null;
      archive_error: string | null;
      dropbox_accountant_path: string | null;
      accountant_error: string | null;
    }>(`/api/invoices/${id}/archive`, { method: 'POST' }),
  remove: (id: string, reason: string) =>
    request<{ ok: boolean; id: string; reason: string }>(`/api/invoices/${id}`, {
      method: 'DELETE',
      body: JSON.stringify({ reason }),
    }),
  bulkDelete: (ids: string[], reason: string) =>
    request<{
      requested: number;
      deleted: number;
      skipped: Array<{ id: string; reason: string }>;
      reason: string;
    }>(`/api/invoices/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify({ ids, reason }),
    }),
};

// ─── Suppliers ────────────────────────────────────────────────────────────
export const suppliersApi = {
  list: (params: { q?: string; limit?: number; offset?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.q) search.set('q', params.q);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString();
    return request<{ suppliers: Supplier[]; total: number; limit: number; offset: number }>(
      `/api/suppliers${qs ? `?${qs}` : ''}`,
    );
  },
  get: (id: string) => request<{ supplier: Supplier }>(`/api/suppliers/${id}`),
  update: (id: string, payload: object) =>
    request<{ supplier: Supplier }>(`/api/suppliers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  create: (payload: object) =>
    request<{ supplier: Supplier }>(`/api/suppliers`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
};

// ─── Reference data ───────────────────────────────────────────────────────
export const referenceDataApi = {
  get: () => request<ReferenceData>('/api/reference-data'),
};

export interface ZohoCustomer {
  id: string;
  name: string;
  company: string | null;
  email: string | null;
}

// ─── Zoho Books (vendors / items / accounts / last-bill / customers) ──────
export const zohoApi = {
  vendors: () =>
    request<{ vendors: ZohoVendor[]; total: number }>('/api/zoho/vendors'),
  customers: () =>
    request<{ customers: ZohoVendor[]; total: number }>('/api/zoho/customers'),
  items: () =>
    request<{ items: ZohoItem[]; total: number }>('/api/zoho/items'),
  accounts: () =>
    request<{ accounts: ZohoAccount[]; total: number }>('/api/zoho/accounts'),
  lastBill: (vendorId: string) =>
    request<{ bill: ZohoBillTemplate | null; has_history: boolean }>(
      `/api/zoho/vendors/${vendorId}/last-bill`,
    ),
  searchCustomers: (q: string) =>
    request<{ customers: ZohoCustomer[] }>(
      `/api/zoho/customers/search?q=${encodeURIComponent(q)}`,
    ),
};

import type { CurrencyRate } from '../types';

// ─── Currency rate ────────────────────────────────────────────────────────
export const currencyApi = {
  rate: (from: string) =>
    request<CurrencyRate>(`/api/currency/rate?from=${encodeURIComponent(from)}`),
};

// ─── Auto-facturation Experts ─────────────────────────────────────────────
export type ExpertsDraftKind = 'external' | 'internal_kestio';

export interface ExpertsPreviewLine {
  client_id: string;
  client_name: string;
  hours: number;
  session_ids: string[];
}

export interface ExpertsPreviewDraft {
  kind: ExpertsDraftKind;
  suggested_next_number: string | null;
  default_unit_price: number | null;
  warnings: string[];
  lines: ExpertsPreviewLine[];
  total_hours: number;
}

export interface ExpertsPreviewConsultant {
  consultant_id: string;
  consultant_name: string;
  vendor_books_id: string | null;
  vendor_books_name: string | null;
  last_bill_number: string | null;
  last_bill_date: string | null;
  default_vat_rate: number | null;
  drafts: ExpertsPreviewDraft[];
}

export interface ExpertsPreviewResponse {
  month: string;
  period: { start: string; end: string };
  consultants: ExpertsPreviewConsultant[];
}

export interface ExpertsGenerateLine {
  client_id: string;
  client_name: string;
  hours: number;
  unit_price: number;
  session_ids: string[];
}
export interface ExpertsGenerateDraft {
  kind: ExpertsDraftKind;
  invoice_number: string;
  lines: ExpertsGenerateLine[];
}
export interface ExpertsGenerateConsultant {
  consultant_id: string;
  consultant_name: string;
  vendor_books_id: string;
  vat_rate: number | null;
  drafts: ExpertsGenerateDraft[];
}
export interface ExpertsGenerateBody {
  month: string;
  consultants: ExpertsGenerateConsultant[];
}
export interface ExpertsGenerateResult {
  consultant_id: string;
  consultant_name: string;
  kind: ExpertsDraftKind;
  status: 'success' | 'partial' | 'failed' | 'skipped';
  bill_id: string | null;
  bill_number: string | null;
  bill_already_existed: boolean;
  dropbox_expert_path: string | null;
  dropbox_accountant_path: string | null;
  crm_sessions_updated: number;
  errors: string[];
}
export interface ExpertsGenerateResponse {
  month: string;
  bill_date: string;
  results: ExpertsGenerateResult[];
}

export interface ExpertsHistoryBill {
  id: string;
  consultant_crm_id: string;
  consultant_name: string;
  vendor_books_id: string;
  kind: ExpertsDraftKind;
  bill_id: string | null;
  bill_number: string;
  bill_date: string;
  month: string;
  total_amount_ht: number | null;
  vat_rate: number | null;
  status: 'success' | 'partial' | 'failed';
  bill_already_existed: boolean;
  dropbox_expert_path: string | null;
  dropbox_accountant_path: string | null;
  crm_sessions_updated: number;
  errors: string[];
  created_at: string;
  created_by: string | null;
}

export interface ExpertsHistoryResponse {
  count: number;
  bills: ExpertsHistoryBill[];
}

export const expertsApi = {
  preview: (month: string) =>
    request<ExpertsPreviewResponse>(`/api/experts/preview?month=${encodeURIComponent(month)}`),
  generate: (body: ExpertsGenerateBody) =>
    request<ExpertsGenerateResponse>(`/api/experts/generate`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  history: (params: { month?: string; consultant_id?: string; status?: string; limit?: number } = {}) => {
    const search = new URLSearchParams();
    if (params.month) search.set('month', params.month);
    if (params.consultant_id) search.set('consultant_id', params.consultant_id);
    if (params.status) search.set('status', params.status);
    if (params.limit) search.set('limit', String(params.limit));
    const qs = search.toString();
    return request<ExpertsHistoryResponse>(`/api/experts/history${qs ? `?${qs}` : ''}`);
  },
  billableSessions: (vendorId: string, month: string) =>
    request<ExpertsBillableSessionsResponse>(
      `/api/experts/billable-sessions?vendor_id=${encodeURIComponent(vendorId)}&month=${encodeURIComponent(month)}`,
    ),
};

export interface ExpertsBillableClient {
  crm_account_id: string;
  client_name: string;
  client_books_id: string | null;
  hours: number;
  session_count: number;
}
export interface ExpertsBillableSessionsResponse {
  consultant: { id: string; name: string } | null;
  month: string;
  clients: ExpertsBillableClient[];
  warning?: string;
}

// ═══ Module Tickets dev (alertes Sentry poussées par BizCoach) ═══════════════

export type SentryAlertStatus = 'a_traiter' | 'pris_en_charge' | 'realise' | 'deploye';
export type SentryScore = 'high' | 'medium' | 'low' | null;

/**
 * Une alerte telle que renvoyée par /api/sentry/alerts. Les champs racine sont
 * dénormalisés (lecture rapide) ; `payload` contient l'objet complet reçu de
 * BizCoach (issue/signals/vulgarization/fixPrompt).
 */
export interface SentryAlert {
  id: string;
  short_id: string;
  title: string | null;
  level: string | null;
  score: SentryScore;
  first_party: boolean | null;
  vulgarization: string | null;
  permalink: string | null;
  status: SentryAlertStatus;
  date_deploiement: string | null;
  num_commit: string | null;
  generated_at: string | null;
  received_at: string;
  updated_at: string;
  payload: SentryPayload | null;
}

export interface SentryPayload {
  source?: string;
  generatedAt?: string;
  issue?: {
    shortId?: string;
    title?: string;
    culprit?: string;
    permalink?: string;
    level?: string;
    firstSeen?: string;
    lastSeen?: string;
  };
  signals?: {
    firstParty?: boolean;
    recurring?: boolean;
    count?: number;
    userCount?: number;
    level?: string;
    environment?: string | null;
    handled?: boolean | null;
    score?: string;
    [k: string]: unknown;
  };
  vulgarization?: string;
  fixPrompt?: string;
  [k: string]: unknown;
}

export interface SentryAlertsResponse {
  count: number;
  alerts: SentryAlert[];
}

export interface SentryStatusUpdateBody {
  status: SentryAlertStatus;
  date_deploiement?: string; // requis si status === 'deploye'
  num_commit?: string; // requis si status === 'deploye'
}

export const sentryApi = {
  list: (status?: SentryAlertStatus) =>
    request<SentryAlertsResponse>(
      `/api/sentry/alerts${status ? `?status=${encodeURIComponent(status)}` : ''}`,
    ),
  updateStatus: (id: string, body: SentryStatusUpdateBody) =>
    request<{ ok: true; alert: SentryAlert }>(`/api/sentry/alerts/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
};

// ═══ Module Paiements Fournisseurs — Bénéficiaires (Phase 1) ═════════════════

export type BeneficiaireType = 'fournisseur' | 'client';

export interface Beneficiaire {
  id: string;
  type: BeneficiaireType;
  vendor_id: string | null;
  vendor_name: string;
  iban: string;
  bic: string | null;
  bank_name: string | null;
  bank_country: string | null;
  titulaire: string | null;
  clearing_code: string | null;
  intermediary_bic: string | null;
  adresse_rue: string | null;
  adresse_cp: string | null;
  adresse_ville: string | null;
  adresse_etat: string | null;
  adresse_pays: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

/** Champs éditables (création / modification). */
export interface BeneficiaireWrite {
  type?: BeneficiaireType;
  vendor_id?: string | null;
  vendor_name: string;
  iban: string;
  bic?: string | null;
  bank_name?: string | null;
  bank_country?: string | null;
  titulaire?: string | null;
  clearing_code?: string | null;
  intermediary_bic?: string | null;
  adresse_rue?: string | null;
  adresse_cp?: string | null;
  adresse_ville?: string | null;
  adresse_etat?: string | null;
  adresse_pays?: string | null;
}

export interface ImportPreviewRow {
  index: number;
  vendor_name: string;
  titulaire: string | null;
  iban: string;
  bic: string | null;
  bank_name: string | null;
  bank_country: string | null;
  clearing_code: string | null;
  intermediary_bic: string | null;
  adresse_rue: string | null;
  adresse_cp: string | null;
  adresse_ville: string | null;
  adresse_etat: string | null;
  adresse_pays: string | null;
  valid: boolean;
  duplicate_in_db: boolean;
  duplicate_in_file: boolean;
  errors: string[];
}

export interface ImportPreviewResponse {
  rows: ImportPreviewRow[];
  summary: { total: number; importable: number; invalid: number; duplicates: number };
}

export interface ImportResult {
  imported: number;
  skipped: number;
  errors: Array<{ vendor_name: string; iban: string; error: string }>;
}

export const beneficiairesApi = {
  list: (opts?: { q?: string; type?: BeneficiaireType }) => {
    const s = new URLSearchParams();
    if (opts?.q) s.set('q', opts.q);
    if (opts?.type) s.set('type', opts.type);
    const qs = s.toString();
    return request<{ beneficiaires: Beneficiaire[]; total: number }>(
      `/api/beneficiaires${qs ? `?${qs}` : ''}`,
    );
  },
  create: (body: BeneficiaireWrite) =>
    request<{ beneficiaire: Beneficiaire }>(`/api/beneficiaires`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  update: (id: string, body: Partial<BeneficiaireWrite>) =>
    request<{ beneficiaire: Beneficiaire }>(`/api/beneficiaires/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  remove: (id: string) =>
    request<{ ok: true; id: string }>(`/api/beneficiaires/${id}`, { method: 'DELETE' }),
  importPreview: (csv: string) =>
    request<ImportPreviewResponse>(`/api/beneficiaires/import/preview`, {
      method: 'POST',
      body: JSON.stringify({ csv }),
    }),
  import: (rows: BeneficiaireWrite[]) =>
    request<ImportResult>(`/api/beneficiaires/import`, {
      method: 'POST',
      body: JSON.stringify({ beneficiaires: rows }),
    }),
};

// ═══ Module Paiements Fournisseurs — Paiements (Phase 2) ═════════════════════

export type PaiementStatus = 'pending' | 'submitted' | 'validated' | 'exported' | 'paid';

export interface Paiement {
  id: string;
  /** null pour les lignes de reprise / saisie manuelle (cf. source_bill_id). */
  bill_id: string | null;
  source_bill_id?: string | null;
  bill_number: string;
  vendor_id: string | null;
  vendor_name: string;
  beneficiaire_id: string | null;
  montant: number;
  date_facture: string | null;
  date_echeance: string | null;
  date_paiement: string | null;
  banque: string | null;
  type_charge: string | null;
  reference: string | null;
  status: PaiementStatus;
  submitted_at: string | null;
  submitted_by: string | null;
  validated_at: string | null;
  validated_by: string | null;
  /** Valideur choisi à la soumission ; null = valideur par défaut. */
  valideur_email?: string | null;
  /** Enrichis par « À valider » : le valideur qui doit statuer. */
  valideur_effectif?: string;
  valideur_nom?: string;
  export_batch_id: string | null;
  rejet_motif?: string | null;
  /** Réponse de l'admin au motif de refus (souvent une question du validateur). */
  reponse_admin?: string | null;
  reponse_at?: string | null;
  reponse_by?: string | null;
  /** 'zoho' (synchro) | 'reprise' (solde dû après correction) | 'manuel' | 'virement_interne'. */
  origine?: 'zoho' | 'reprise' | 'manuel' | 'virement_interne';
  origine_motif?: string | null;
  created_at: string;
  updated_at: string;
  // Enrichi côté API :
  beneficiaire: { id: string; vendor_name: string; iban: string; bic: string | null } | null;
  iban_manquant: boolean;
}

export interface SyncResult {
  ok: true;
  total_virement: number;
  created: number;
  updated: number;
  skipped: number;
  /** Lignes recréées pour un solde resté dû après correction dans Books. */
  reprises?: number;
}

export interface VirementInterneInput {
  emettrice: string;
  beneficiaire: string;
  montant: number;
  date: string;
}

export interface PaiementManuelInput {
  beneficiaire_id: string;
  bill_number: string;
  montant: number;
  origine_motif: string;
  banque?: string | null;
  type_charge?: string | null;
  date_facture?: string | null;
  date_echeance?: string | null;
  date_paiement?: string | null;
  source_bill_id?: string | null;
}

/** Un valideur des paiements et remboursements (liste réglée dans Paiements > Paramètres). */
export interface ValideurPaiements {
  email: string;
  nom: string;
}

/** Une ligne soumise et le valideur choisi pour elle. */
export interface LigneSoumise {
  id: string;
  valideur_email?: string;
}

export interface SoumissionResult {
  submitted: number;
  blocked: Array<{ id: string; reason: string }>;
  email_sent: boolean;
  par_valideur?: Array<{ email: string; nom: string; lignes: number }>;
}

/** 'toutes' : tout ADMINISTRATEUR (règle du 14/10/2026) ; 'mes_lignes' : un valideur qui ne l'est pas. */
export type PorteeValidation = 'mes_lignes' | 'toutes' | 'aucune';

export const paiementsApi = {
  valideurs: () => request<{ valideurs: ValideurPaiements[] }>(`/api/paiements/valideurs`),
  sync: () => request<SyncResult>(`/api/paiements/sync`),
  aPayer: () => request<{ paiements: Paiement[]; total: number }>(`/api/paiements/a-payer`),
  validation: () =>
    request<{ paiements: Paiement[]; total: number; portee: PorteeValidation }>(`/api/paiements/validation`),
  update: (
    id: string,
    body: { banque?: string | null; date_paiement?: string | null; reponse_admin?: string | null },
  ) => request<{ paiement: Paiement }>(`/api/paiements/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  creerManuel: (body: PaiementManuelInput) =>
    request<{ paiement: Paiement }>(`/api/paiements/manuel`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  creerVirementInterne: (body: VirementInterneInput) =>
    request<{ paiement: Paiement }>(`/api/paiements/virement-interne`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  supprimer: (id: string) =>
    request<{ ok: true; deleted: string }>(`/api/paiements/${id}`, { method: 'DELETE' }),
  submit: (lignes: LigneSoumise[]) =>
    request<SoumissionResult>(`/api/paiements/submit`, { method: 'POST', body: JSON.stringify({ lignes }) }),
  validate: (id: string) =>
    request<{ ok: true; validated: number }>(`/api/paiements/${id}/validate`, { method: 'POST' }),
  validateAll: (ids: string[]) =>
    request<{ ok: true; validated: number }>(`/api/paiements/validate-all`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  reject: (ids: string[], motif: string) =>
    request<{ rejected: number; email_sent: boolean }>('/api/paiements/reject', {
      method: 'POST',
      body: JSON.stringify({ ids, motif }),
    }),
  enCours: () => request<{ paiements: PaiementEnCours[]; total: number }>(`/api/paiements/en-cours`),
  confirm: () =>
    request<{ ok: true; validated: number; email_sent: boolean }>(`/api/paiements/confirm`, { method: 'POST' }),
  // Aperçu de la facture d'un paiement (base64). available:false si aucun PDF.
  pdf: (id: string) =>
    request<{ available: boolean; pdf_base64?: string; source?: string; filename?: string }>(
      `/api/paiements/${id}/pdf`,
    ),
};

// ═══ Module Paiements Fournisseurs — Export XML SEPA (Phase 3) ═══════════════

export interface ExportGroup {
  source: 'paiement' | 'remboursement';
  banque: string;
  type_charge: string | null;
  /** Virements internes seulement : la date demandée à la banque. */
  date_execution?: string;
  nb: number;
  montant_total: number;
}

export interface ExportBatch {
  id: string;
  banque: string;
  type_charge: string | null;
  nb_transactions: number;
  montant_total: number;
  xml_filename: string;
  xml_path: string | null;
  status: 'generated' | 'uploaded' | 'completed';
  date_depot: string | null;
  created_at: string;
  created_by: string | null;
}

export interface ExportBatchFilters {
  status?: string;
  banque?: string;
  depot_from?: string;
  depot_to?: string;
}

export interface ExportPreview {
  groups: ExportGroup[];
  batches: ExportBatch[];
}

/** Paiement enrichi du lot d'export (écran « En cours de règlement »). */
export interface PaiementEnCours extends Paiement {
  batch: { id: string; xml_filename: string; status: string; banque: string } | null;
}

export const exportApi = {
  preview: (filters: ExportBatchFilters = {}) => {
    const search = new URLSearchParams();
    if (filters.status) search.set('status', filters.status);
    if (filters.banque) search.set('banque', filters.banque);
    if (filters.depot_from) search.set('depot_from', filters.depot_from);
    if (filters.depot_to) search.set('depot_to', filters.depot_to);
    const qs = search.toString();
    return request<ExportPreview>(`/api/export/preview${qs ? `?${qs}` : ''}`);
  },
  generate: (
    banque: string,
    type_charge: string | null,
    source: 'paiement' | 'remboursement' = 'paiement',
    date_execution?: string,
  ) =>
    request<{ batch: ExportBatch }>(`/api/export/generate`, {
      method: 'POST',
      body: JSON.stringify({ banque, type_charge, source, date_execution }),
    }),
  // Téléchargement : lien direct (cookie de session same-origin).
  downloadUrl: (id: string) => `/api/export/${id}/download`,
  archive: (id: string) => request<{ batch: ExportBatch }>(`/api/export/${id}/archive`, { method: 'POST' }),
  complete: (id: string, date_depot: string) =>
    request<{ ok: true; paid: number; date_depot: string }>(`/api/export/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ date_depot }),
    }),
};

// ═══ Correspondances nom extrait → vendor Zoho (vendor_aliases) ══════════════

export interface VendorAlias {
  id: string;
  extracted_name: string;
  zoho_vendor_id: string;
  zoho_vendor_name: string;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export const vendorAliasesApi = {
  list: () => request<{ aliases: VendorAlias[]; total: number }>(`/api/vendor-aliases`),
  upsert: (body: { extracted_name: string; zoho_vendor_id: string; zoho_vendor_name: string }) =>
    request<{ alias: VendorAlias }>(`/api/vendor-aliases`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// ═══ Paramètres applicatifs (Admin) ══════════════════════════════════════════

export interface AppSettings {
  paiements_bill_date_min: string; // YYYY-MM-DD
}

export const adminSettingsApi = {
  valideurs: () =>
    request<{ valideurs: ValideurPaiements[]; candidats: ValideurPaiements[] }>(`/api/admin/settings/valideurs`),
  majValideurs: (valideurs: ValideurPaiements[]) =>
    request<{ ok: true; valideurs: ValideurPaiements[] }>(`/api/admin/settings/valideurs`, {
      method: 'PUT',
      body: JSON.stringify({ valideurs }),
    }),
  get: () => request<AppSettings>(`/api/admin/settings`),
  update: (body: AppSettings) =>
    request<{ ok: true } & AppSettings>(`/api/admin/settings`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
};

// ═══ Remboursements d'avoirs clients ═════════════════════════════════════════

export interface Remboursement {
  id: string;
  credit_note_id: string;
  credit_note_number: string;
  customer_id: string | null;
  customer_name: string;
  beneficiaire_id: string | null;
  montant: number;
  date_avoir: string | null;
  date_remboursement: string | null;
  banque: string | null;
  reference: string | null;
  status: PaiementStatus;
  submitted_at: string | null;
  submitted_by: string | null;
  validated_at: string | null;
  validated_by: string | null;
  valideur_email?: string | null;
  valideur_effectif?: string;
  valideur_nom?: string;
  export_batch_id: string | null;
  rejet_motif?: string | null;
  /** Réponse de l'admin au motif de refus (souvent une question du validateur). */
  reponse_admin?: string | null;
  reponse_at?: string | null;
  reponse_by?: string | null;
  created_at: string;
  updated_at: string;
  beneficiaire: { id: string; vendor_name: string; iban: string; bic: string | null } | null;
  iban_manquant: boolean;
}

export interface RembSyncResult {
  ok: true;
  total_open: number;
  created: number;
  updated: number;
  skipped: number;
}

export const remboursementsApi = {
  sync: () => request<RembSyncResult>(`/api/remboursements/sync`),
  list: () => request<{ remboursements: Remboursement[]; total: number }>(`/api/remboursements`),
  validation: () =>
    request<{ remboursements: Remboursement[]; total: number; portee: PorteeValidation }>(
      `/api/remboursements/validation`,
    ),
  update: (
    id: string,
    body: { banque?: string | null; date_remboursement?: string | null; reponse_admin?: string | null },
  ) =>
    request<{ remboursement: Remboursement }>(`/api/remboursements/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  submit: (lignes: LigneSoumise[]) =>
    request<SoumissionResult>(`/api/remboursements/submit`, { method: 'POST', body: JSON.stringify({ lignes }) }),
  validate: (id: string) =>
    request<{ ok: true; validated: number }>(`/api/remboursements/${id}/validate`, { method: 'POST' }),
  validateAll: (ids: string[]) =>
    request<{ ok: true; validated: number }>(`/api/remboursements/validate-all`, {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),
  reject: (ids: string[], motif: string) =>
    request<{ rejected: number; email_sent: boolean }>('/api/remboursements/reject', {
      method: 'POST',
      body: JSON.stringify({ ids, motif }),
    }),
  confirm: () =>
    request<{ ok: true; validated: number; email_sent: boolean }>(`/api/remboursements/confirm`, { method: 'POST' }),
};
